import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import './TomorrowsMealsPage.css'; // Your CSS file for styling

function TomorrowsMealsPage() {
  const [allMeals, setAllMeals] = useState([]);
  const [summary, setSummary] = useState({
    totalToDeliver: 0,
    riceCounts: {},
    hubBreakdown: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // This function now specifically calculates summaries ONLY from active meals
    const processMealDataForSummary = (mealList) => {
      const activeMeals = mealList.filter(meal => meal.status !== 'Cancelled');

      const summaryData = {
        totalToDeliver: activeMeals.length,
        riceCounts: {},
        hubBreakdown: {},
      };

      for (const meal of activeMeals) {
        const rice = meal.selectedRice || 'Unknown';
        const hub = meal.hub || 'Unknown Hub';

        // Tally rice counts
        summaryData.riceCounts[rice] = (summaryData.riceCounts[rice] || 0) + 1;

        // Tally hub breakdowns
        if (!summaryData.hubBreakdown[hub]) {
          summaryData.hubBreakdown[hub] = {
            total: 0,
            rice: {},
          };
        }
        summaryData.hubBreakdown[hub].total++;
        summaryData.hubBreakdown[hub].rice[rice] = (summaryData.hubBreakdown[hub].rice[rice] || 0) + 1;
      }

      return summaryData;
    };

    const fetchMealsForDate = async () => {
      setLoading(true);
      setError('');
      try {
        // Query for all meals on August 22, 2025
        const targetDate = new Date('2025-08-22T00:00:00'); 
        const startOfDay = new Date(targetDate.setHours(0, 0, 0, 0));
        const endOfDay = new Date(targetDate.setHours(23, 59, 59, 999));

        const mealsCollection = collection(db, 'selected_meal');
        const q = query(
          mealsCollection,
          where('selectionDate', '>=', Timestamp.fromDate(startOfDay)),
          where('selectionDate', '<=', Timestamp.fromDate(endOfDay))
        );

        const querySnapshot = await getDocs(q);
        const mealsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Set state for the table to show ALL meals
        setAllMeals(mealsList);
        
        // Calculate and set the summary from the same list
        const processedSummary = processMealDataForSummary(mealsList);
        setSummary(processedSummary);

      } catch (err) {
        console.error("Failed to fetch meals:", err);
        setError('Could not load meal data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMealsForDate();
  }, []);

  if (loading) {
    return <div className="loading-container"><h2>Loading Meal Data...</h2></div>;
  }

  if (error) {
    return <div className="error-container"><h2>{error}</h2></div>;
  }

  return (
    <div className="page-container">
      <h1>Meal Plan for August 22, 2025</h1>

      {/* --- Summary Section --- */}
      <div className="summary-section card">
        <h2>Delivery Summary (Active Meals Only)</h2>
        <p><strong>Total Meals to Deliver:</strong> {summary.totalToDeliver}</p>
        
        <div className="summary-breakdown">
          <h3>Rice Variety Breakdown (Total)</h3>
          <ul>
            {Object.keys(summary.riceCounts).length > 0 ? (
              Object.entries(summary.riceCounts).map(([rice, count]) => (
                <li key={rice}><strong>{rice}:</strong> {count} meals</li>
              ))
            ) : (
              <li>No active meals to deliver.</li>
            )}
          </ul>
        </div>
        
        <div className="summary-breakdown">
          <h3>Hub Delivery Breakdown</h3>
          {Object.keys(summary.hubBreakdown).length > 0 ? (
            Object.entries(summary.hubBreakdown).map(([hub, data]) => (
              <div key={hub} className="hub-card">
                <h4>Hub: {hub} (Total: {data.total} meals)</h4>
                <ul>
                  {Object.entries(data.rice).map(([rice, count]) => (
                    <li key={rice}><strong>{rice}:</strong> {count}</li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <p>No active deliveries for any hub.</p>
          )}
        </div>
      </div>

      {/* --- Detailed Table Section --- */}
      <div className="details-section card">
        <h2>All Selections for Date ({allMeals.length} records)</h2>
        <table>
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Hub</th>
              <th>Selected Rice</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {allMeals.length > 0 ? (
              allMeals.map(meal => (
                <tr key={meal.id}>
                  <td>{meal.customerName || 'N/A'}</td>
                  <td>{meal.hub || 'N/A'}</td>
                  <td>{meal.selectedRice || 'N/A'}</td>
                  <td>{meal.status || 'Confirmed'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center' }}>
                  No meals found for the specified date.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TomorrowsMealsPage;